import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePublicSettings } from "@/lib/siteSettings";
import { Sparkles, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Trial urgency banner - real expiration, not fake scarcity.
 * - Active trial: "X days left in your Pro trial"
 * - Last 2 days: amber warning about readiness tracking becoming limited
 * - Ended: one-time dismissible "Your Pro trial has ended" card
 */
const TrialBanner = () => {
  const navigate = useNavigate();
  const { user, isTrialing, trialDaysLeft, trialEnded } = useAuth();
  const settings = usePublicSettings();
  const freemiumV2 = settings["features.freemium_v2"] === true;
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("passadc_trial_ended_dismissed") === "1"
  );

  if (!user || user.role === "admin") return null;

  if (trialEnded && !dismissed) {
    return (
      <div data-testid="trial-ended-banner" className="glass rounded-2xl border border-border p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <p className="font-semibold mb-0.5">Your Pro trial has ended.</p>
          <p className="text-sm text-muted-foreground">
            You can continue with PassADC Free (10 questions/day, all lessons, your progress) or unlock Pro to keep full mocks, weakness analysis and the unlimited AI tutor.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button data-testid="trial-ended-upgrade-btn" onClick={() => navigate("/billing")} className="rounded-full">
            <Sparkles className="w-4 h-4 mr-1.5" /> Unlock Pro
          </Button>
          <Button
            data-testid="trial-ended-dismiss-btn"
            variant="outline"
            className="rounded-full"
            onClick={() => {
              localStorage.setItem("passadc_trial_ended_dismissed", "1");
              setDismissed(true);
            }}
          >
            Stay on Free
          </Button>
        </div>
      </div>
    );
  }

  if (!isTrialing) return null;
  // Under freemium v2 the TrialUnlockBanner owns the active-trial message.
  if (freemiumV2) return null;

  const urgent = trialDaysLeft <= 2;
  return (
    <div
      data-testid="trial-countdown-banner"
      className={`glass rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
        urgent ? "border-warning/40 bg-warning/5" : "border-primary/25 bg-primary/5"
      }`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${urgent ? "bg-warning/15" : "bg-primary/15"}`}>
        {urgent ? <AlertTriangle className="w-4.5 h-4.5 text-warning" /> : <Clock className="w-4.5 h-4.5 text-primary" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">
          {trialDaysLeft === 1 ? "Last day of your Pro trial" : `${trialDaysLeft} days left in your Pro trial`}
        </p>
        <p className="text-xs text-muted-foreground">
          {urgent
            ? `Your personalised readiness tracking becomes limited in ${trialDaysLeft === 1 ? "1 day" : `${trialDaysLeft} days`}.`
            : "Everything is unlocked - full question bank, mocks, weakness analysis and the AI tutor."}
        </p>
      </div>
      <Button
        data-testid="trial-banner-upgrade-btn"
        size="sm"
        variant={urgent ? "default" : "outline"}
        className="rounded-full flex-shrink-0"
        onClick={() => navigate("/billing")}
      >
        <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Keep Pro
      </Button>
    </div>
  );
};

export default TrialBanner;
